import { createSimpleContext } from "@mux/ui/context";
import { createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { useAuth } from "@/context/auth";
import {
  createPushMutationQueue,
  createPushOperationGuard,
  idlePushStatus,
  MobilePushError,
  type MobilePushStatus,
  pushError,
  shouldRepairPushSubscription,
  withPushRequestTimeout,
  withPushTimeout,
} from "@/context/mobile-push-controller";
import {
  clearPendingPushDetach,
  pushServiceWorkerRegistration,
  queuePendingPushDetach,
  readPendingPushDetaches,
  readPushBindingToken,
  readPushEnabledPreference,
  readPushRegistrationFingerprint,
  pushRegistrationIsFresh,
  writePushBindingToken,
  writePushEnabledPreference,
  writePushRegistrationFingerprint,
} from "@/context/push-device";
import { useSettings } from "@/context/settings";
import {
  detachPushSubscription,
  getPushConfiguration,
  pushDeviceStatus,
  testBackgroundPush,
  PushOwnershipConflict,
  registerPushSubscription,
} from "@/transport/push";

export type { MobilePushStatus } from "@/context/mobile-push-controller";

const BROWSER_TIMEOUT_MS = 10_000;
const NETWORK_TIMEOUT_MS = 15_000;
const PUSH_REFRESH_INTERVAL_MS = 6 * 60 * 60_000;

const PUSH_REGISTRATION_LOCK = "mux-push-registration";

async function withPushRegistrationLock<T>(task: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(PUSH_REGISTRATION_LOCK, task);
  }
  return task();
}

function decodeBase64Url(value: string) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

function supportsMobilePush() {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function sameApplicationServerKey(subscription: PushSubscription, publicKey: string) {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const expected = decodeBase64Url(publicKey);
  const actual = new Uint8Array(current);
  return actual.byteLength === expected.byteLength && actual.every((value, index) => value === expected[index]);
}

export const { use: useMobilePush, provider: MobilePushProvider } = createSimpleContext({
  name: "MobilePush",
  init: () => {
    const settings = useSettings();
    const auth = useAuth();
    const [status, setStatus] = createSignal<MobilePushStatus>(supportsMobilePush() ? "checking" : "unsupported");
    const [enabled, setEnabled] = createSignal(false);
    const [busy, setBusy] = createSignal(false);
    const [error, setError] = createSignal<string>();
    const operations = createPushOperationGuard();
    const browserMutations = createPushMutationQueue();
    let lifecycle: Promise<unknown> = Promise.resolve();
    let preferenceSync = 0;
    let registration: ServiceWorkerRegistration | undefined;
    let configuration: Awaited<ReturnType<typeof getPushConfiguration>> | undefined;
    const [delivery, setDelivery] = createSignal<{ status: string; error: string }>({ status: "unverified", error: "" });
    let active = true;

    const runExclusive = <T,>(task: () => Promise<T>) => {
      const result = lifecycle.then(task, task);
      lifecycle = result.then(() => undefined, () => undefined);
      return result;
    };

    const publish = (operation: number, next: MobilePushStatus, message?: string) => {
      if (!active || !operations.current(operation)) return false;
      setStatus(next);
      setError(message);
      return true;
    };

    const ensureRegistration = async () => {
      registration ??= await withPushTimeout(pushServiceWorkerRegistration(), "service-worker", BROWSER_TIMEOUT_MS);
      return registration;
    };
    const loadPublicKey = async () => {
      configuration = await withPushRequestTimeout((signal) => getPushConfiguration(signal), "public-key", NETWORK_TIMEOUT_MS);
      return configuration.publicKey;
    };

    const owner = () => auth.user()?.id ?? "";
    const registrationFingerprint = (subscription: PushSubscription) => JSON.stringify({
      audience: configuration?.audience,
      endpoint: subscription.endpoint,
      owner: owner(),
      agent: settings.notifications.agent(),
      permissions: settings.notifications.permissions(),
      errors: settings.notifications.errors(),
    });

    const saveSubscription = async (subscription: PushSubscription, subscriptionOwner = owner()) => {
      if (!configuration) await loadPublicKey();
      const body = subscription.toJSON();
      const bindingToken = await withPushRequestTimeout((signal) => registerPushSubscription({
        appOrigin: configuration!.appOrigin, audience: configuration!.audience,
        endpoint: body.endpoint,
        keys: body.keys,
        bindingToken: readPushBindingToken(),
        agent: settings.notifications.agent(),
        permissions: settings.notifications.permissions(),
        errors: settings.notifications.errors(),
      }, signal), "registration", NETWORK_TIMEOUT_MS);
      if (bindingToken) writePushBindingToken(bindingToken);
      writePushRegistrationFingerprint(registrationFingerprint(subscription));
      clearPendingPushDetach(subscriptionOwner, subscription.endpoint);
    };

    const detachServerSubscription = async (endpoint: string, subscriptionOwner = owner()) => {
      await withPushRequestTimeout(
        (signal) => detachPushSubscription(endpoint, signal),
        "disable",
        NETWORK_TIMEOUT_MS,
      );
      clearPendingPushDetach(subscriptionOwner, endpoint);
    };

    const detachPendingSubscriptions = async (currentEndpoint?: string, currentOwner = owner()) => {
      let firstFailure: unknown;
      for (const pending of readPendingPushDetaches(currentOwner)) {
        if (pending.endpoint === currentEndpoint) {
          clearPendingPushDetach(currentOwner, pending.endpoint);
          continue;
        }
        try {
          await detachServerSubscription(pending.endpoint, currentOwner);
        } catch (cause) {
          firstFailure ??= cause;
        }
      }
      if (firstFailure) throw firstFailure;
    };

    const getBrowserSubscription = (registered: ServiceWorkerRegistration) =>
      withPushTimeout(
        browserMutations.run(() => registered.pushManager.getSubscription()),
        "subscription",
        BROWSER_TIMEOUT_MS,
      );

    const removeBrowserSubscription = (subscription: PushSubscription, stage: "subscription" | "disable") =>
      withPushTimeout(browserMutations.run(() => subscription.unsubscribe()), stage, BROWSER_TIMEOUT_MS);

    const createSubscription = async (registered: ServiceWorkerRegistration, publicKey: string) =>
      withPushTimeout(
        browserMutations.run(() => registered.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: decodeBase64Url(publicKey),
        })),
        "subscription",
        BROWSER_TIMEOUT_MS,
      );

    const replaceSubscription = async (subscription: PushSubscription, publicKey?: string) => {
      const registered = await ensureRegistration();
      const key = publicKey ?? await loadPublicKey();
      const previousEndpoint = subscription.endpoint;
      const subscriptionOwner = owner();
      queuePendingPushDetach(subscriptionOwner, previousEndpoint);
      const removed = await removeBrowserSubscription(subscription, "subscription");
      if (!removed) throw new MobilePushError("subscription", "The browser did not remove its outdated notification subscription.");
      setEnabled(false);
      writePushBindingToken("");
      const replacement = await createSubscription(registered, key);
      setEnabled(true);
      await saveSubscription(replacement, subscriptionOwner);
      await detachPendingSubscriptions(replacement.endpoint, subscriptionOwner);
      return replacement;
    };

    const reconcileSubscription = (subscription: PushSubscription) =>
      withPushRegistrationLock(async () => {
        if (
          readPushBindingToken() &&
          readPushRegistrationFingerprint() === registrationFingerprint(subscription) &&
          pushRegistrationIsFresh(PUSH_REFRESH_INTERVAL_MS)
        ) {
          return subscription;
        }
        try {
          await saveSubscription(subscription);
          await detachPendingSubscriptions(subscription.endpoint);
          return subscription;
        } catch (cause) {
          const original = cause instanceof MobilePushError ? cause.original : cause;
          if (!(original instanceof PushOwnershipConflict)) throw cause;
          return replaceSubscription(subscription);
        }
      });

    const inspect = async (operation: number) => {
      if (!supportsMobilePush()) {
        setEnabled(false);
        publish(operation, "unsupported");
        return;
      }
      if (Notification.permission === "denied") {
        setEnabled(false);
        publish(operation, "permission-blocked");
        return;
      }

      const enabledPreference = readPushEnabledPreference();
      try {
        const registered = await ensureRegistration();
        let subscription = await getBrowserSubscription(registered);
        if (!operations.current(operation)) return;
        if (!subscription) {
          setEnabled(false);
          writePushBindingToken("");
          const next = idlePushStatus(true, Notification.permission, enabledPreference, false);
          try {
            await detachPendingSubscriptions();
            publish(operation, next);
          } catch (cause) {
            publish(operation, next, `${pushError("disable", cause).message} Notifications remain off in this browser.`);
          }
          return;
        }

        if (enabledPreference === false) {
          const subscriptionOwner = owner();
          queuePendingPushDetach(subscriptionOwner, subscription.endpoint);
          const removed = await removeBrowserSubscription(subscription, "disable");
          if (!removed) throw new MobilePushError("disable", "The browser did not remove its disabled notification subscription.");
          if (!operations.current(operation)) return;
          writePushBindingToken("");
          setEnabled(false);
          try {
            await detachServerSubscription(subscription.endpoint, subscriptionOwner);
            publish(operation, "disabled");
          } catch (cause) {
            publish(operation, "disabled", `${pushError("disable", cause).message} Notifications remain off in this browser.`);
          }
          return;
        }

        setEnabled(true);
        const publicKey = await loadPublicKey();
        if (!operations.current(operation)) return;
        if (!sameApplicationServerKey(subscription, publicKey)) {
          if (!shouldRepairPushSubscription(Notification.permission, enabledPreference)) {
            publish(operation, "degraded", "This browser has an outdated notification subscription. Enable notifications to replace it.");
            return;
          }
          subscription = await replaceSubscription(subscription, publicKey);
          if (!operations.current(operation)) return;
        } else {
          await reconcileSubscription(subscription);
          if (!operations.current(operation)) return;
        }
        setEnabled(true);
        const deliveryStatus = await withPushRequestTimeout((signal) => pushDeviceStatus(subscription.endpoint, signal), "registration", NETWORK_TIMEOUT_MS);
        if (!operations.current(operation)) return;
        if (deliveryStatus.status === "unregistered") await saveSubscription(subscription);
        setDelivery(deliveryStatus);
        publish(operation, "ready");
      } catch (cause) {
        publish(operation, "degraded", pushError("registration", cause).message);
      }
    };

    const refresh = async () => {
      if (busy()) return;
      const operation = operations.begin();
      await runExclusive(() => inspect(operation));
    };

    const subscribe = async () => {
      if (!supportsMobilePush() || busy()) return false;
      const operation = operations.begin();
      const permissionRequest = Notification.requestPermission();
      setBusy(true);
      return runExclusive(async () => {
        publish(operation, "registering");
        try {
          const permission = await permissionRequest;
          if (!operations.current(operation)) return false;
          if (permission !== "granted") {
            setEnabled(false);
            publish(
              operation,
              permission === "denied" ? "permission-blocked" : "permission-required",
              permission === "default"
                ? "Notification permission was not granted. Allow notifications in the address bar or site settings, then try again."
                : undefined,
            );
            return false;
          }

          const registered = await ensureRegistration();
          const publicKey = await loadPublicKey();
          let subscription = await getBrowserSubscription(registered);
          if (!operations.current(operation)) return false;
          if (subscription && !sameApplicationServerKey(subscription, publicKey)) {
            subscription = await replaceSubscription(subscription, publicKey);
          } else {
            subscription ??= await createSubscription(registered, publicKey);
            setEnabled(true);
            await reconcileSubscription(subscription);
          }
          if (!operations.current(operation)) return false;
          writePushEnabledPreference(true);
          setEnabled(true);
          publish(operation, "ready");
          return true;
        } catch (cause) {
          publish(operation, "degraded", pushError("registration", cause).message);
          return false;
        } finally {
          if (operations.current(operation)) setBusy(false);
        }
      });
    };

    const unsubscribe = async () => {
      if (!supportsMobilePush() || busy()) return false;
      const operation = operations.begin();
      writePushEnabledPreference(false);
      setBusy(true);
      return runExclusive(async () => {
        setError(undefined);
        let endpoint: string | undefined;
        const subscriptionOwner = owner();
        try {
          try {
            const registered = await ensureRegistration();
            const subscription = await getBrowserSubscription(registered);
            endpoint = subscription?.endpoint;
            if (subscription) {
              queuePendingPushDetach(subscriptionOwner, subscription.endpoint);
              const removed = await removeBrowserSubscription(subscription, "disable");
              if (!removed) {
                throw new MobilePushError("disable", "The browser did not remove its notification subscription. Try again.");
              }
            }
            if (!operations.current(operation)) return false;
            writePushBindingToken("");
            setEnabled(false);
            publish(operation, "disabled");
          } catch (cause) {
            publish(operation, "degraded", pushError("disable", cause).message);
            return false;
          }

          try {
            if (endpoint) await detachServerSubscription(endpoint, subscriptionOwner);
            else await detachPendingSubscriptions();
            return true;
          } catch (cause) {
            publish(operation, "disabled", `${pushError("disable", cause).message} Notifications are off in this browser, but Kettle could not clean up its server registration.`);
            return false;
          }
        } finally {
          if (operations.current(operation)) setBusy(false);
        }
      });
    };

    createEffect(() => {
      const preferences = [
        auth.user()?.id,
        settings.notifications.agent(),
        settings.notifications.permissions(),
        settings.notifications.errors(),
      ];
      if (status() !== "ready") return;
      void preferences;
      const request = ++preferenceSync;
      const operation = operations.snapshot();
      void runExclusive(async () => {
        if (request !== preferenceSync || busy() || !operations.current(operation)) return;
        try {
          const registered = await ensureRegistration();
          const subscription = await getBrowserSubscription(registered);
          if (!subscription || request !== preferenceSync || !operations.current(operation)) return;
          await reconcileSubscription(subscription);
        } catch (cause) {
          publish(operation, "degraded", pushError("registration", cause).message);
        }
      });
    });

    const visible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    let refreshTimer: ReturnType<typeof setInterval> | undefined;
    onMount(() => {
      void refresh();
      refreshTimer = setInterval(() => {
        if (!active || busy()) return;
        const needsRefresh = status() === "ready" || status() === "degraded" || readPendingPushDetaches(owner()).length > 0;
        if (needsRefresh) void refresh();
      }, PUSH_REFRESH_INTERVAL_MS);
      document.addEventListener("visibilitychange", visible);
    });
    onCleanup(() => {
      active = false;
      operations.invalidate();
      if (refreshTimer !== undefined) clearInterval(refreshTimer);
      document.removeEventListener("visibilitychange", visible);
    });

    const testPush = async () => {
      const registered = await ensureRegistration();
      const subscription = await getBrowserSubscription(registered);
      if (!subscription) throw new Error("Enable background push first.");
      await loadPublicKey();
      await reconcileSubscription(subscription);
      const id = await withPushRequestTimeout((signal) => testBackgroundPush(subscription.endpoint, signal), "registration", NETWORK_TIMEOUT_MS);
      setDelivery({ status: "queued", error: "" });
      return id;
    };
    return { status, enabled, busy, error, delivery, testPush, subscribe, unsubscribe, refresh };
  },
});
